import { IPaginationProps } from '../../types/base.interface';
import { ICreateEvent, IEvent, IUserEvent } from '../../types/event.interface';
import { IUserAttempts } from '../../types/problem.interface';
import { IUsers } from '../../types/user.interface';
import { api } from '../api';

export interface IStartEvent {
  eventId: string;
  language: string;
}

export interface IGetUserAttempts extends IPaginationProps {
  eventId: string;
  reviewed: boolean;
}

export interface IGetUsersWhoNotStarted extends IPaginationProps {
  id: string;
}

export interface IFinishUserEvent {
  eventId: string;
}

export interface IGrantEventAccessForGroup extends IFinishUserEvent {
  groupId: string;
}

export interface IAddEventToUsers {
  id: string;
  userIds: string[];
}

export const eventApi = api.injectEndpoints({
  endpoints: builder => ({
    getEventsOfUser: builder.query<IEvent[], void>({
      query: () => `/users/events`,
      providesTags: (result, error, arg) =>
        result ? [...result.map(({ id }) => ({ type: 'UserEvents' as const, id })), 'UserEvents'] : ['UserEvents'],
    }),
    getTeacherEvents: builder.query<IEvent[], void>({
      query: () => `/events/teacher/events`,
      providesTags: (result, error, arg) =>
        result ? [...result.map(({ id }) => ({ type: 'Events' as const, id })), 'Events'] : ['Events'],
    }),
    searchAttempts: builder.query<{ items: any[]; number: number }, { user?: string; ev?: string; date?: string; status?: string; scoreMin?: number; scoreMax?: number; page?: number; size?: number; sortBy?: string; sortDir?: 'asc'|'desc' }>({
      query: ({ user, ev, date, status, scoreMin, scoreMax, page = 1, size = 50, sortBy, sortDir }) => ({ url: `/user-events/search`, params: { user, ev, date, status, scoreMin, scoreMax, page, size, sortBy, sortDir } }),
      providesTags: ['UsersAttempts'],
    }),
    searchEvents: builder.query<IEvent[], { q?: string; limit?: number }>({
      query: ({ q = '', limit = 10 }) => ({ url: `/events/search`, params: { q, limit } }),
      providesTags: ['Events'],
    }),
    getEvents: builder.query<IEvent[], void>({
      query: () => `/events`,
      providesTags: (result, error, arg) =>
        result ? [...result.map(({ id }) => ({ type: 'Events' as const, id })), 'Events'] : ['Events'],
    }),
    getUserEvents: builder.query<IUserEvent[], void>({
      query: () => `/users/user-events`,
      providesTags: (result, error, arg) =>
        result
          ? [...result.map(({ eventId }) => ({ type: 'UserEvents' as const, eventId })), 'UserEvents']
          : ['UserEvents'],
    }),
    // Получение полной информации о попытке пользователя для события (для админа/преподавателя)
    getUserEventByIds: builder.query<IUserEvent, { eventId: string; userId: string }>({
      query: ({ eventId, userId }) => `/user-events/${eventId}/${userId}`,
      providesTags: (result, error, arg) => ['UsersAttempts'],
    }),
    getEvent: builder.query<IEvent, string>({
      query: id => `/events/${id}`,
      providesTags: (result, error, arg) =>
        result ? [{ type: 'Events' as const, id: result.id }] : ['Events'],
    }),
    finishUserEvent: builder.mutation<void, IFinishUserEvent>({
      query: ({ eventId }) => ({
        url: `/user-events/finish/${eventId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['UserEvents'],
    }),
    reopenUserEvent: builder.mutation<void, { eventId: string; userId: string }>({
      query: ({ eventId, userId }) => ({ url: `/user-events/reopen/${eventId}/${userId}`, method: 'PUT' }),
      invalidatesTags: ['UserEvents'],
    }),
    finishUserEventForUser: builder.mutation<void, { eventId: string; userId: string }>({
      query: ({ eventId, userId }) => ({ url: `/user-events/finish/${eventId}/${userId}`, method: 'PUT' }),
      invalidatesTags: ['UserEvents'],
    }),
    deleteUserEvent: builder.mutation<void, { eventId: string; userId: string }>({
      query: ({ eventId, userId }) => ({ url: `/user-events/${eventId}/${userId}`, method: 'DELETE' }),
      invalidatesTags: ['UserEvents'],
    }),
    updateProblemPoints: builder.mutation<void, { eventId: string; userId: string; problemId: string; points: number }>({
      query: ({ eventId, userId, problemId, points }) => ({
        url: `/user-events/${eventId}/${userId}/score/${problemId}/${points}`,
        method: 'PUT',
      }),
      invalidatesTags: ['UserEvents', 'UsersAttempts'],
    }),
    createEvent: builder.mutation<{ id: string }, ICreateEvent>({
      query: body => ({
        url: `/events`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['UserEvents', 'Events'],
    }),
    addEventToUsers: builder.mutation<void, IAddEventToUsers>({
      query: ({ id, userIds }) => ({
        url: `/events/${id}/users`,
        method: 'PUT',
        body: userIds,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['UserEvents', 'Events'],
    }),
    grantEventAccessForGroup: builder.mutation<void, IGrantEventAccessForGroup>({
      query: ({ eventId, groupId }) => ({
        url: `/events/${eventId}/group/${groupId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['UserEvents', 'Events'],
    }),
    removeEvent: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/events/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Events'],
    }),
    startEvent: builder.mutation<void, IStartEvent>({
      query: ({ eventId, language }) => ({
        url: `/events/${eventId}/start/${language}`,
        method: 'PUT',
      }),
      invalidatesTags: ['UserEvents'],
    }),
    getUserAttempts: builder.query<IUserAttempts, IGetUserAttempts>({
      query: ({ eventId, reviewed, pageNumber, pageSize }) =>
        `/user-events/${eventId}/user-attempts/${reviewed}/${pageNumber}/${pageSize}`,
      providesTags: ['UsersAttempts'],
    }),
    getUsersWhoNotStarted: builder.query<IUsers, IGetUsersWhoNotStarted>({
      query: ({ id, pageNumber, pageSize }) => `/events/${id}/users-who-not-started-event/${pageNumber}/${pageSize}`,
      providesTags: ['UsersWhoNotStart'],
    }),
    getEventUsers: builder.query<IUsers, { id: string; pageNumber: number; pageSize: number }>({
      query: ({ id, pageNumber, pageSize }) => `/events/${id}/users/${pageNumber}/${pageSize}`,
      providesTags: ['Events'],
    }),
    updateEvent: builder.mutation<void, { id: string; name: string; description: string; type: string; startTime: string; endTime: string; duration: string; minimumPointsToPass: number; groupId?: string | null }>({
      query: ({ id, ...body }) => ({
        url: `/events/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Events', 'UserEvents'],
    }),
    addProblemToEvent: builder.mutation<void, { eventId: string; hash: string; points: number }>({
      query: ({ eventId, hash, points }) => ({
        url: `/events/${eventId}/problem`,
        method: 'PUT',
        body: {
          hash,
          points,
        },
      }),
      invalidatesTags: ['Events'],
    }),
    removeProblemFromEvent: builder.mutation<void, { eventId: string; hash: string }>({
      query: ({ eventId, hash }) => ({
        url: `/events/${eventId}/problem/${hash}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Events'],
    }),
    removeUserFromEvent: builder.mutation<void, { eventId: string; userId: string }>({
      query: ({ eventId, userId }) => ({
        url: `/events/${eventId}/users`,
        method: 'DELETE',
        body: [userId],
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Events'],
    }),
    removeEventFromGroup: builder.mutation<void, { eventId: string; groupId: string }>({
      query: ({ eventId, groupId }) => ({
        url: `/events/${eventId}/group/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Events'],
    }),
    getUsersInEvent: builder.query<IUsers, { eventId: string; pageNumber: number; pageSize: number }>({
      query: ({ eventId, pageNumber, pageSize }) => `/events/${eventId}/users/${pageNumber}/${pageSize}`,
      providesTags: ['Events'],
    }),
  }),
});
